/**
 * "Tap anywhere else to close": while a popup is open, a tap or click outside it (or Escape) closes it.
 * Pointer events, because iPhone does not send mouse/click events for taps on plain areas. Elements
 * marked data-popup-toggle (the button that opens the popup) are left to their own click.
 */
import { useEffect, type RefObject } from 'react';

export function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, onClose: () => void, opts: { swallow?: boolean } = {}): void {
  const swallow = !!opts.swallow;
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target || ref.current?.contains(target)) return;
      if (target.closest?.('[data-popup-toggle]')) return;
      onClose();
      // swallow: the tap only closes the popup; it does not also press what is underneath.
      if (swallow) {
        const block = (ev: Event) => (ev.preventDefault(), ev.stopPropagation());
        document.addEventListener('click', block, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', block, true), 700);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, onClose, swallow]);
}
